import { useState, useEffect, useRef } from 'react';
import axios, { AxiosError } from 'axios';
import { Search, Check, Instagram } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FacebookLogo, GoogleLogo, Ga4Logo } from "@/data/logo";
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store';
import { setBrands } from '@/store/slices/BrandSlice';


interface PlatformModalProps {
  platform: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandId: string;
  onSuccess?: (platform: string, accountName: string, accountId: string) => void;
}

interface GoogleAdsAccount {
  name: string;
  clientId: string;
  managerId?: string;
  hidden?: boolean;
}


interface GoogleAnalyticsAccount {
  propertyName: string;
  propertyId: string;
}

interface FacebookAdsAccount {
  adname: string;
  id: string;
}

export default function PlatformModal({
  platform,
  open,
  onOpenChange,
  brandId,
  onSuccess
}: PlatformModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [googleAdsAccounts, setGoogleAdsAccounts] = useState<GoogleAdsAccount[]>([]);
  const [googleAnalyticsAccounts, setGoogleAnalyticsAccounts] = useState<GoogleAnalyticsAccount[]>([]);
  const [facebookAdsAccounts, setFacebookAdsAccounts] = useState<FacebookAdsAccount[]>([]);
  const [instagramAccounts, setInstagramAccounts] = useState<FacebookAdsAccount[]>([]);
  const [showLoginButton, setShowLoginButton] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [connectedAccounts, setConnectedAccounts] = useState<string[]>([]);
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);

  // Shopify (single store) connection state
  const [shopifyStoreName, setShopifyStoreName] = useState('');
  const [isShopifyConnecting, setIsShopifyConnecting] = useState(false);
  const [shopifyError, setShopifyError] = useState<string | null>(null);
  const shopifyPopupTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const baseURL = import.meta.env.PROD ? import.meta.env.VITE_API_URL : import.meta.env.VITE_LOCAL_API_URL;
  const dispatch = useDispatch();
  const brands = useSelector((state: RootState) => state.brand.brands);
  const currentBrand = brands.find((b: any) => b._id === brandId);
  const isShopifyConnected = !!currentBrand?.shopifyAccount?.shopifyAccessToken;
  const connectedShopDomain = currentBrand?.shopifyAccount?.shopName;

  // Check URL parameters for modal opening
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const modalToOpen = params.get('openModal');
    
    if (modalToOpen && onOpenChange) {
      switch (modalToOpen.toLowerCase()) {
        case 'googleads':
          if (platform.toLowerCase() === 'google ads') {
            onOpenChange(true);
          }
          break;
        case 'googleanalytics':
          if (platform.toLowerCase() === 'google analytics') {
            onOpenChange(true);
          }
          break;
        case 'facebook':
          if (platform.toLowerCase() === 'facebook') {
            onOpenChange(true);
          }
          break;
        case 'instagram':
          if (platform.toLowerCase() === 'instagram') {
            onOpenChange(true);
          }
          break;
      }
      
      // Remove the modal parameter from URL
      params.delete('openModal');
      const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [platform, onOpenChange]);

  useEffect(() => {
    const fetchConnectedAccounts = async () => {
      try {
        const response = await axios.get(`${baseURL}/api/brands/${brandId}`, {
          withCredentials: true,
        });
        const brand = response.data;

        const connectedIds: string[] = [];
        if (brand.fbAdAccounts?.length) connectedIds.push(...brand.fbAdAccounts);
        if (brand.igAccountIds?.length) connectedIds.push(...brand.igAccountIds);
        if (brand.googleAdAccount) connectedIds.push(brand.googleAdAccount);
        if (brand.ga4Account?.PropertyID) connectedIds.push(brand.ga4Account.PropertyID);

        setConnectedAccounts(connectedIds);
      } catch (error) {
        console.error('Error fetching connected accounts:', error);
      }
    };

    if (open && brandId) {
      fetchConnectedAccounts();
    }
  }, [brandId, open]);

  // When Shopify OAuth completes in the popup, the backend posts a message to the parent window.
  useEffect(() => {
    if (!open) return;
    if (platform.toLowerCase() !== 'shopify') return;
    if (!brandId) return;

    const handleMessage = async (event: MessageEvent) => {
      const data = event.data as any;
      if (!data || typeof data !== 'object') return;

      if (data.type === 'shopify_brand_setup_complete' && data.brandId === brandId) {
        if (shopifyPopupTimeoutRef.current) {
          clearTimeout(shopifyPopupTimeoutRef.current);
          shopifyPopupTimeoutRef.current = null;
        }

        setIsShopifyConnecting(false);
        setShopifyError(null);

        // Refresh the updated brand in Redux
        try {
          const response = await axios.get(`${baseURL}/api/brands/${brandId}`, { withCredentials: true });
          const updatedBrand = response.data;

          const updatedBrands = brands.map((b: any) => (b._id === brandId ? updatedBrand : b));
          dispatch(setBrands(updatedBrands));

          // onSuccess is optional, but we follow the same signature as other platforms.
          onSuccess?.('Shopify', data.shopDisplayName || updatedBrand?.shopifyAccount?.shopName || 'Shopify', String(data.shopId));
        } catch (err) {
          // If refresh fails, still close the modal since the brand was updated server-side.
          console.error('Failed to refresh brand after Shopify OAuth:', err);
          onSuccess?.('Shopify', data.shopDisplayName || 'Shopify', String(data.shopId));
        } finally {
          onOpenChange(false);
        }
      }

      if (data.type === 'shopify_brand_setup_error' && data.brandId === brandId) {
        if (shopifyPopupTimeoutRef.current) {
          clearTimeout(shopifyPopupTimeoutRef.current);
          shopifyPopupTimeoutRef.current = null;
        }
        setIsShopifyConnecting(false);
        setShopifyError(data.message || 'Failed to connect Shopify.');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [open, platform, brandId, baseURL, brands, dispatch, onSuccess, onOpenChange]);

  useEffect(() => {
    const fetchAccounts = async () => {
      if (!open) return;

      // Shopify doesn't use the accounts-list flow.
      if (platform.toLowerCase() === 'shopify') {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        let endpoint = '';
        let accountsSetter: any = null;

        switch (platform.toLowerCase()) {
          case 'google ads':
            endpoint = `/api/setup/google-accounts/${brandId}`;
            accountsSetter = setGoogleAdsAccounts;
            break;
          case 'google analytics':
            endpoint = `/api/setup/ga4-propertyIds/${brandId}`;
            accountsSetter = setGoogleAnalyticsAccounts;
            break;
          case 'facebook':
            endpoint = `/api/setup/fb-ad-accounts/${brandId}`;
            accountsSetter = setFacebookAdsAccounts;
            break;
          case 'instagram':
            endpoint = `/api/setup/ig-accounts/${brandId}`;
            accountsSetter = setInstagramAccounts;
            break;
        }
        console.log(`Calling API: ${baseURL}${endpoint} with brandId: ${brandId}`);

        if (!endpoint) {
          throw new Error('Invalid platform');
        }
        const response = await axios.get(
          `${baseURL}${endpoint}`,
          { withCredentials: true }
        );

        if (response.status === 200) {
          setShowLoginButton(false);
          if (platform.toLowerCase() === 'google ads') {
            accountsSetter(response.data?.clientAccounts.filter((account: GoogleAdsAccount) => !account.hidden));
          } else if (platform.toLowerCase() === 'google analytics') {
            accountsSetter(response.data.propertiesList || []);
          } else if (platform.toLowerCase() === 'facebook') {
            accountsSetter(response.data.adAccounts || []);
          } else if (platform.toLowerCase() === 'instagram') {
            accountsSetter(response.data.adAccounts || []);
          }
        }
      } catch (error) {
        handleError(error as AxiosError, setShowLoginButton);
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
  }, [platform, brandId, open]);

  const handleError = (error: unknown, setShowLoginButton: (value: boolean) => void) => {
    const axiosError = error as AxiosError;

    if (axiosError.response) {
      const { status, data } = axiosError.response;
      const code = (data as any)?.code;
      
      // Check for Facebook OAuth authorization error
      if (status === 401 && (code === 'OAUTH_NOT_AUTHORIZED' || code === 'TOKEN_EXPIRED')) {
        if ((platform.toLowerCase() === 'facebook' || platform.toLowerCase() === 'instagram') && code === 'OAUTH_NOT_AUTHORIZED') {
          setErrorMsg(`${platform} authorization has expired. Please reconnect.`);
          setShowLoginButton(true);
        } else {
          setErrorMsg((data as any)?.message || 'Authentication expired. Please log in again.');
        }
      } else if (status === 400) {
        setErrorMsg((data as any)?.message || 'Bad request. Please check your inputs.');
      } else if (status === 403) {
        setErrorMsg(`Please connect your ${platform} account to view available profiles.`);
        setShowLoginButton(true);
      } else if (status === 404) {
        setErrorMsg((data as any)?.message || `No ${platform} accounts found. Please reconnect and ensure you have granted the correct permissions.`);
        setShowLoginButton(true);
      } else {
        console.error('Unhandled Error Status:', status);
        console.error('Error Message:', data);
      }
    } else if (axiosError.request) {
      console.error('No response received:', axiosError.request);
    } else {
      console.error('Unexpected Error:', axiosError.message);
    }
  };

  const handlePlatformLogin = () => {
    const p = platform.toLowerCase();
    if (p === 'facebook') handleFbLogin();
    else if (p === 'instagram') handleIgLogin();
    else if (p === 'google ads') handleGoogleAdLogin();
    else if (p === 'google analytics') handleGoogleAnalyticsLogin();
  };

  const updateBrandWithAccount = async (accountIds: string[], accountNames: string[], managerIds: (string | undefined)[]) => {
    try {
      let updateData = {};
      
      switch (platform.toLowerCase()) {
        case 'facebook':
          // Get current brand to append to existing accounts
          const currentBrand = brands.find(brand => brand._id === brandId);
          const existingFbAccounts = currentBrand?.fbAdAccounts || [];
          const newFbAccounts = [...new Set([...existingFbAccounts, ...accountIds])];
          updateData = { fbAdAccounts: newFbAccounts };
          break;
        case 'instagram':
          const currentBrandIg = brands.find(brand => brand._id === brandId);
          const existingIgAccounts = currentBrandIg?.igAccountIds || [];
          const newIgAccounts = [...new Set([...existingIgAccounts, ...accountIds])];
          updateData = { igAccountIds: newIgAccounts };
          break;
        case 'google ads':
          // Get current brand to append to existing accounts
          const currentBrandForGoogle = brands.find(brand => brand._id === brandId);
          const existingGoogleAccounts = currentBrandForGoogle?.googleAdAccount || [];
          const newGoogleAccounts = accountIds.map((accountId, index) => ({
            clientId: accountId,
            managerId: managerIds[index] || ''
          }));
          // Merge existing and new accounts, avoiding duplicates
          const mergedGoogleAccounts = [...existingGoogleAccounts];
          newGoogleAccounts.forEach(newAccount => {
            const exists = mergedGoogleAccounts.some(existing => 
              existing.clientId === newAccount.clientId && existing.managerId === newAccount.managerId
            );
            if (!exists) {
              mergedGoogleAccounts.push({
                clientId: newAccount.clientId,
                managerId: newAccount.managerId || ''
              });
            }
          });
          updateData = { googleAdAccount: mergedGoogleAccounts };
          break;
        case 'google analytics':
          // For GA4, we typically only have one account, but we can support multiple
          const currentBrandForGA = brands.find(brand => brand._id === brandId);
          const existingGAAccount = currentBrandForGA?.ga4Account?.PropertyID;
          if (!existingGAAccount) {
            updateData = { ga4Account: { PropertyID: accountIds[0] } };
          } else {
            // If GA4 account already exists, we might want to replace it or handle differently
            updateData = { ga4Account: { PropertyID: accountIds[0] } };
          }
          break;
      }
  
      const response = await axios.patch(
        `${baseURL}/api/brands/update/${brandId}`,
        updateData,
        { withCredentials: true }
      );
  
      if (response.status === 200) {
        
        const updatedBrand = response.data;
        
        
        const updatedBrands = brands.map(brand => 
          brand._id === brandId ? updatedBrand : brand
        );
        
        dispatch(setBrands(updatedBrands));
        
        // Call onSuccess for each account
        accountIds.forEach((accountId, index) => {
          onSuccess?.(platform, accountNames[index], accountId);
        });
        
        // Don't automatically close modal - let handleConnectSelected handle it
        // setSelectedAccounts([]);
        // onOpenChange(false);
      }
    } catch (error) {
      console.error('Error updating brand:', error);
    }
  };

  const handleAccountSelection = (accountId: string) => {
    setSelectedAccounts(prev => {
      if (prev.includes(accountId)) {
        return prev.filter(id => id !== accountId);
      } else {
        return [...prev, accountId];
      }
    });
  };

  const handleConnectSelected = async () => {
    if (selectedAccounts.length === 0) return;

    const selectedAccountData = selectedAccounts.map(accountId => {
      const account = filteredAccounts()?.find(acc => {
        if (platform.toLowerCase() === 'google ads') {
          return (acc as GoogleAdsAccount).clientId === accountId;
        } else if (platform.toLowerCase() === 'facebook' || platform.toLowerCase() === 'instagram') {
          return (acc as FacebookAdsAccount).id === accountId;
        } else {
          return (acc as GoogleAnalyticsAccount).propertyId === accountId;
        }
      });

      if (!account) return null;

      let displayName: string;
      let managerId: string | undefined;

      if ('clientId' in account) {
        managerId = account.managerId;
        displayName = managerId
          ? `${account.name} (${account.clientId}/${managerId})`
          : `${account.name} (${account.clientId})`;
      } else if ('propertyId' in account) {
        displayName = `${account.propertyName} (${account.propertyId})`;
      } else if ('id' in account) {
        displayName = `${account.adname}`;
      } else {
        return null;
      }

      return { accountId, displayName, managerId };
    }).filter(Boolean);

    if (selectedAccountData.length > 0) {
      const accountIds = selectedAccountData.map(data => data!.accountId);
      const accountNames = selectedAccountData.map(data => data!.displayName);
      const managerIds = selectedAccountData.map(data => data!.managerId);

      await updateBrandWithAccount(accountIds, accountNames, managerIds);
      
      // Show success message
      console.log(`Successfully connected ${selectedAccountData.length} account(s)`);
      
      // Reset selected accounts and close modal
      setSelectedAccounts([]);
      onOpenChange(false);
    }
  };

  const getOAuthSource = (modalKey: string) => {
    const params = new URLSearchParams();
    params.set('reconnectBrand', brandId);
    params.set('openModal', modalKey);
    return `${window.location.pathname}?${params.toString()}`;
  };

  const handleGoogleAdLogin = async () => {
    try {
      const response = await axios.get(
        `${baseURL}/api/auth/google?context=googleAdSetup&source=${encodeURIComponent(getOAuthSource('googleAds'))}`, 
        { withCredentials: true }
      );
      const { authUrl } = response.data;
      window.location.href = authUrl;
    } catch (error) {
      console.error('Error getting Google Auth URL:', error);
    }
  };

  const handleGoogleAnalyticsLogin = async () => {
    try {
      const response = await axios.get(
        `${baseURL}/api/auth/google?context=googleAnalyticsSetup&source=${encodeURIComponent(getOAuthSource('googleAnalytics'))}`, 
        { withCredentials: true }
      );
      const { authUrl } = response.data;
      window.location.href = authUrl;
    } catch (error) {
      console.error('Error getting Google Auth URL:', error);
    }
  };

  const handleFbLogin = async () => {
    try {
      const response = await axios.get(
        `${baseURL}/api/auth/facebook?source=${encodeURIComponent(getOAuthSource('facebook'))}`, 
        { withCredentials: true }
      );
      if (response.data.success) {
        window.location.href = response.data.authURL;
      }
    } catch (error) {
      console.error('Error getting Facebook Auth URL:', error);
    }
  };

  const handleIgLogin = async () => {
    try {
      const response = await axios.get(
        `${baseURL}/api/auth/instagram?source=${encodeURIComponent(getOAuthSource('instagram'))}`, 
        { withCredentials: true }
      );
      if (response.data.success) {
        window.location.href = response.data.authURL;
      }
    } catch (error) {
      console.error('Error getting Instagram Auth URL:', error);
    }
  };

  const handleShopifyLogin = async () => {
    const cleanInput = shopifyStoreName.trim();
    if (!cleanInput) return;

    setIsShopifyConnecting(true);
    setShopifyError(null);

    try {
      const response = await axios.post(
        `${baseURL}/api/auth/shopify/connect-brand`,
        {
          shop: cleanInput,
          brandId
        },
        { withCredentials: true }
      );

      const { authUrl } = response.data || {};
      if (!authUrl) {
        throw new Error('Failed to generate Shopify authorization URL');
      }

      const popup = window.open(authUrl, 'shopify_oauth', 'width=900,height=800');
      if (!popup) {
        // Fallback: if popups are blocked, use full navigation.
        window.location.href = authUrl;
        return;
      }

      // Fallback timeout if the popup can't postMessage for some reason.
      if (shopifyPopupTimeoutRef.current) clearTimeout(shopifyPopupTimeoutRef.current);
      shopifyPopupTimeoutRef.current = setTimeout(() => {
        setIsShopifyConnecting(false);
        setShopifyError('Timed out waiting for Shopify connection. Please try again.');
        shopifyPopupTimeoutRef.current = null;
      }, 120000);
    } catch (error) {
      console.error('Error getting Shopify Auth URL:', error);
      setIsShopifyConnecting(false);
      setShopifyError('Failed to connect Shopify. Please try again.');
    }
  };

  const filteredAccounts = () => {
    if (platform.toLowerCase() === 'google ads') {
      if (!googleAdsAccounts || googleAdsAccounts.length === 0) {
        return [];
      }
      return googleAdsAccounts.filter((account) => {
        const searchLower = searchTerm.toLowerCase();
        return (
          (account.name && account.name.toLowerCase().includes(searchLower)) ||
          (account.clientId && account.clientId.toLowerCase().includes(searchLower)) ||
          (account.managerId && account.managerId.toLowerCase().includes(searchLower))
        );
      });
    } else if (platform.toLowerCase() === 'google analytics') {
      if (!googleAnalyticsAccounts || googleAnalyticsAccounts.length === 0) {
        return [];
      }
      return googleAnalyticsAccounts.filter((account) => {
        const searchLower = searchTerm.toLowerCase();
        return (
          (account.propertyName && account.propertyName.toLowerCase().includes(searchLower)) ||
          (account.propertyId && account.propertyId.toLowerCase().includes(searchLower))
        );
      });
    } else if (platform.toLowerCase() === 'facebook') {
      if (!facebookAdsAccounts || facebookAdsAccounts.length === 0) {
        return [];
      }
      return facebookAdsAccounts.filter((account) => {
        const searchLower = searchTerm.toLowerCase();
        return (
          (account.adname && account.adname.toLowerCase().includes(searchLower)) ||
          (account.id && account.id.toLowerCase().includes(searchLower))
        );
      });
    } else if (platform.toLowerCase() === 'instagram') {
      if (!instagramAccounts || instagramAccounts.length === 0) {
        return [];
      }
      return instagramAccounts.filter((account) => {
        const searchLower = searchTerm.toLowerCase();
        return (
          (account.adname && account.adname.toLowerCase().includes(searchLower)) ||
          (account.id && account.id.toLowerCase().includes(searchLower))
        );
      });
    } else {
      return [];
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Connect {platform} Account</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {platform.toLowerCase() === 'shopify' ? (
            <div className="space-y-3">
              <div className="space-y-2">
                <Input
                  type="text"
                  placeholder="Enter your Shopify store name (e.g., mystore)"
                  value={shopifyStoreName}
                  onChange={(e) => setShopifyStoreName(e.target.value)}
                />
                {isShopifyConnected && connectedShopDomain && (
                  <p className="text-sm text-gray-500">
                    Current store: <span className="font-medium">{connectedShopDomain}</span>
                  </p>
                )}
              </div>

              {shopifyError && <p className="text-sm text-red-600">{shopifyError}</p>}

              <Button
                className="w-full bg-green-700 hover:bg-green-800"
                onClick={handleShopifyLogin}
                disabled={!shopifyStoreName.trim() || isShopifyConnecting}
              >
                {isShopifyConnecting ? 'Connecting...' : 'Login to Shopify'}
              </Button>

              <p className="text-sm text-gray-500 text-center">
                Enter your store name without <span className="font-medium">.myshopify.com</span>
              </p>
            </div>
          ) : loading ? (
            <div className="flex justify-center py-4">Loading...</div>
          ) : showLoginButton ? (
            <div className="space-y-3">
              <div className="bg-amber-50 p-4 rounded-md border border-amber-200">
                <p className="text-sm text-amber-800 mb-4">
                  {errorMsg || `Please connect your ${platform} account to view available profiles.`}
                </p>
                <Button
                  size="sm"
                  onClick={handlePlatformLogin}
                  className="w-full sm:w-auto bg-white text-slate-700 border-slate-200 hover:bg-slate-50 flex items-center gap-2 border"
                >
                  {platform.toLowerCase() === 'google ads' && <GoogleLogo height="1rem" width="1rem" />}
                  {platform.toLowerCase() === 'google analytics' && <Ga4Logo height="1rem" width="1rem" />}
                  {platform.toLowerCase() === 'facebook' && <FacebookLogo height="1rem" width="1rem" />}
                  {platform.toLowerCase() === 'instagram' && <Instagram height="1rem" width="1rem" className="text-pink-600" />}
                  {errorMsg?.includes('expired') ? `Reconnect ${platform} Account` : `Connect ${platform} Account`}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search accounts..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              {/* Selected accounts section - sticky at top */}
              {selectedAccounts.length > 0 && (
                <div className="sticky top-0 z-10 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-blue-800">
                      {selectedAccounts.length} account(s) selected
                    </span>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedAccounts([]);
                          onOpenChange(false);
                        }}
                        className="border-gray-300 text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleConnectSelected}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Save ({selectedAccounts.length})
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Scrollable accounts list */}
              <div className="max-h-[50vh] overflow-auto">
                {(() => {
                  const filtered = filteredAccounts();  
                  if (!filtered || filtered.length === 0) {
                    return (
                      <div className="text-center py-8 space-y-4">
                        <p className="text-gray-500">
                          {searchTerm ? 'No accounts found matching your search.' : 'No accounts available.'}
                        </p>
                        {!searchTerm && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={handlePlatformLogin}
                            className="mx-auto flex items-center gap-2"
                          >
                            {platform.toLowerCase() === 'facebook' && <FacebookLogo height="1rem" width="1rem" />}
                            {platform.toLowerCase() === 'instagram' && <Instagram height="1rem" width="1rem" className="text-pink-600" />}
                            {platform.toLowerCase() === 'google ads' && <GoogleLogo height="1rem" width="1rem" />}
                            {platform.toLowerCase() === 'google analytics' && <Ga4Logo height="1rem" width="1rem" />}
                            Reconnect {platform}
                          </Button>
                        )}
                      </div>
                    );
                  }
                  
                  return filtered.map((account, _) => {
                    const isGoogleAds = platform.toLowerCase() === 'google ads';
                    const isFacebook = platform.toLowerCase() === 'facebook';
                    const isInstagram = platform.toLowerCase() === 'instagram';

                    const accountId = isGoogleAds
                      ? (account as GoogleAdsAccount).clientId
                      : (isFacebook || isInstagram)
                        ? (account as FacebookAdsAccount).id
                        : (account as GoogleAnalyticsAccount).propertyId;

                    const managerId = isGoogleAds
                      ? (account as GoogleAdsAccount).managerId
                      : undefined;

                    const accountName = isGoogleAds
                      ? (account as GoogleAdsAccount).name
                      : (isFacebook || isInstagram)
                        ? (account as FacebookAdsAccount).adname
                        : (account as GoogleAnalyticsAccount).propertyName;

                    // Create unique key to prevent duplicates
                    const uniqueKey = isGoogleAds && managerId 
                      ? `${accountId}-${managerId}`
                      : accountId;

                    const platformLogo = isGoogleAds ? (
                      <GoogleLogo height="1rem" width="1rem" />
                    ) : isFacebook ? (
                      <FacebookLogo height="1rem" width="1rem" />
                    ) : isInstagram ? (
                      <Instagram height="1rem" width="1rem" />
                    ) : (
                      <Ga4Logo height="1rem" width="1rem" />
                    );

                    const isConnected = connectedAccounts.includes(accountId);
                    const isSelected = selectedAccounts.includes(accountId);

                    const displayText = isGoogleAds && managerId
                      ? `${accountName} (${accountId}/${managerId})`
                      : (isFacebook || isInstagram)
                        ? `${accountName}`
                        : `${accountName} (${accountId})`;

                    return (
                      <div
                        key={uniqueKey}
                        className="flex items-center justify-between p-2 rounded hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-3">
                     
                          <span>
                            <div className="flex flex-row items-center gap-3">
                              {platformLogo}
                              {displayText}
                            </div>
                          </span>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleAccountSelection(accountId)}
                          disabled={isConnected}
                          className={isConnected ? "bg-gray-400" : isSelected ? "bg-green-600" : "bg-blue-600"}
                        >
                          {isConnected && <Check className="h-4 w-4 mr-2" />}
                          {isConnected ? 'Connected' : isSelected ? 'Selected' : 'Select'}
                        </Button>
                      </div>
                    );
                  });
                })()}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}